/**
 * @license
 * Copyright (c) 2021-present, FriendliAI Inc. All rights reserved.
 */

import React from 'react';
import { redirect, type RouteObject } from 'react-router-dom';

import {
  checkIsEndpointUpdatable,
  redirectToLandingIfPlanInaccessible,
  redirectToProjectIfPlanAccessible,
} from '@fai/friendli-suite/features/routes/loaders/product/dedicated-endpoints';
import { hideOptionFromRelease } from '@fai/friendli-suite/shared/environments/hide-from-release';
import { FlagValidationWrapper } from '@fai/friendli-suite/shared/launch-darkly';
import DEProjectBlockByInvalidPaymentLayout from '@fai/friendli-suite/views/layout/product/dedicated-endpoints/projects/block-by-invalid-payment-layout';
import ProjectDatasetDetailLayout from '@fai/friendli-suite/views/layout/product/dedicated-endpoints/projects/datasets/layout';
import DEProjectDetailLayout from '@fai/friendli-suite/views/layout/product/dedicated-endpoints/projects/detail-layout';
import ProjectEndpointDetailLayout from '@fai/friendli-suite/views/layout/product/dedicated-endpoints/projects/endpoints/layout';
import UsageValidationWrapper from '@fai/friendli-suite/views/layout/product/dedicated-endpoints/projects/endpoints/usage/layout';
import ProjectFinetuningDetailLayout from '@fai/friendli-suite/views/layout/product/dedicated-endpoints/projects/finetuning/layout';
import ProjectModelDetailLayout from '@fai/friendli-suite/views/layout/product/dedicated-endpoints/projects/models/layout';
import ProductBasicLayout from '@fai/friendli-suite/views/layout/product/product-basic-layout';
import { pipeLoaders } from '@fai/third-party/react-router-dom';
import { redirectIfSessionExpired } from '@fai/third-party/supertokens';

const LandingPage = React.lazy(
  () => import('@fai/friendli-suite/pages/dedicated-endpoints/landing')
);
const ProjectListPage = React.lazy(
  () => import('@fai/friendli-suite/pages/dedicated-endpoints/projects/list')
);
const ProjectEndpointListPage = React.lazy(
  () => import('@fai/friendli-suite/pages/dedicated-endpoints/projects/endpoints/list')
);
const ProjectEndpointCreatePage = React.lazy(
  () => import('@fai/friendli-suite/pages/dedicated-endpoints/projects/endpoints/create')
);
const ProjectEndpointUpdatePage = React.lazy(
  () => import('@fai/friendli-suite/pages/dedicated-endpoints/projects/endpoints/detail/update')
);
const ProjectEndpointOverviewPage = React.lazy(
  () => import('@fai/friendli-suite/pages/dedicated-endpoints/projects/endpoints/detail/overview')
);
const ProjectEndpointPlaygroundPage = React.lazy(
  () => import('@fai/friendli-suite/pages/dedicated-endpoints/projects/endpoints/detail/playground')
);
const ProjectEndpointMetricsPage = React.lazy(
  () => import('@fai/friendli-suite/pages/dedicated-endpoints/projects/endpoints/detail/metrics')
);
const ProjectEndpointLogsPage = React.lazy(
  () => import('@fai/friendli-suite/pages/dedicated-endpoints/projects/endpoints/detail/logs')
);
const ProjectEndpointVersionsPage = React.lazy(
  () => import('@fai/friendli-suite/pages/dedicated-endpoints/projects/endpoints/detail/versions')
);
const ProjectFinetuningListPage = React.lazy(
  () => import('@fai/friendli-suite/pages/dedicated-endpoints/projects/finetuning/list')
);
const ProjectFinetuningCreatePage = React.lazy(
  () => import('@fai/friendli-suite/pages/dedicated-endpoints/projects/finetuning/create')
);
const ProjectFinetuningOverviewPage = React.lazy(
  () => import('@fai/friendli-suite/pages/dedicated-endpoints/projects/finetuning/detail/overview')
);
const ProjectFinetuningOutputModelsPage = React.lazy(
  () =>
    import('@fai/friendli-suite/pages/dedicated-endpoints/projects/finetuning/detail/output-models')
);
const ProjectModelListPage = React.lazy(
  () => import('@fai/friendli-suite/pages/dedicated-endpoints/projects/models/list')
);
const ProjectModelOverviewPage = React.lazy(
  () => import('@fai/friendli-suite/pages/dedicated-endpoints/projects/models/detail/overview')
);
const ProjectModelVersionsPage = React.lazy(
  () => import('@fai/friendli-suite/pages/dedicated-endpoints/projects/models/detail/versions')
);
const ProjectModelEndpointsPage = React.lazy(
  () => import('@fai/friendli-suite/pages/dedicated-endpoints/projects/models/detail/endpoints')
);
const ProjectModelFilesPage = React.lazy(
  () => import('@fai/friendli-suite/pages/dedicated-endpoints/projects/models/detail/files')
);
const ProjectDatasetListPage = React.lazy(
  () => import('@fai/friendli-suite/pages/dedicated-endpoints/projects/datasets/list')
);
const ProjectDatasetCreatePage = React.lazy(
  () => import('@fai/friendli-suite/pages/dedicated-endpoints/projects/datasets/create')
);
const ProjectDatasetOverviewPage = React.lazy(
  () => import('@fai/friendli-suite/pages/dedicated-endpoints/projects/datasets/detail/overview')
);
const ProjectDocumentListPage = React.lazy(
  () => import('@fai/friendli-suite/pages/dedicated-endpoints/projects/documents/list')
);
const ProjectMonthlyUsagePage = React.lazy(
  () => import('@fai/friendli-suite/pages/dedicated-endpoints/projects/monthly-usage')
);
const ProjectSettingsPage = React.lazy(
  () => import('@fai/friendli-suite/pages/dedicated-endpoints/projects/settings')
);

const dedicatedEndpointsRoutes: RouteObject[] = [
  {
    path: 'landing',
    loader: pipeLoaders({
      sequence: [redirectIfSessionExpired, redirectToProjectIfPlanAccessible],
    }),
    Component: ProductBasicLayout,
    children: [{ index: true, Component: LandingPage }],
  },
  {
    path: 'project',
    loader: pipeLoaders({
      sequence: [redirectIfSessionExpired, redirectToLandingIfPlanInaccessible],
    }),
    children: [
      {
        path: 'list',
        Component: ProductBasicLayout,
        children: [
          {
            Component: DEProjectBlockByInvalidPaymentLayout,
            children: [{ index: true, Component: ProjectListPage }],
          },
        ],
      },
      {
        path: ':projectId/*',
        Component: DEProjectDetailLayout,
        children: [
          {
            Component: DEProjectBlockByInvalidPaymentLayout,
            children: [
              {
                path: 'endpoint',
                children: [
                  { path: 'list', Component: ProjectEndpointListPage },
                  { path: 'create', Component: ProjectEndpointCreatePage },
                  {
                    path: ':endpointId/update',
                    loader: checkIsEndpointUpdatable,
                    children: [{ index: true, Component: ProjectEndpointUpdatePage }],
                  },
                  {
                    path: ':endpointId/*',
                    Component: ProjectEndpointDetailLayout,
                    children: [
                      {
                        path: 'overview',
                        Component: ProjectEndpointOverviewPage,
                      },
                      {
                        path: 'playground',
                        Component: ProjectEndpointPlaygroundPage,
                      },
                      {
                        path: 'metrics',
                        Component: () => (
                          <FlagValidationWrapper flagKey="dedicatedEndpointMetrics" />
                        ),
                        children: [
                          {
                            index: true,
                            Component: ProjectEndpointMetricsPage,
                          },
                        ],
                      },
                      {
                        path: 'logs',
                        Component: () => <FlagValidationWrapper flagKey="dedicatedEndpointLogs" />,
                        children: [
                          {
                            Component: UsageValidationWrapper,
                            children: [
                              {
                                index: true,
                                Component: ProjectEndpointLogsPage,
                              },
                            ],
                          },
                        ],
                      },
                      {
                        path: 'versions',
                        Component: () => (
                          <FlagValidationWrapper flagKey="dedicatedEndpointVersions" />
                        ),
                        children: [
                          {
                            index: true,
                            Component: ProjectEndpointVersionsPage,
                          },
                        ],
                      },
                      { path: '*', loader: () => redirect('overview') },
                    ],
                  },
                  { path: '*', loader: () => redirect('list') },
                ],
              },
              {
                path: 'fine-tuning',
                children: [
                  { path: 'list', Component: ProjectFinetuningListPage },
                  { path: 'create', Component: ProjectFinetuningCreatePage },
                  {
                    path: ':finetuningId/*',
                    Component: ProjectFinetuningDetailLayout,
                    children: [
                      {
                        path: 'overview',
                        Component: ProjectFinetuningOverviewPage,
                      },
                      {
                        path: 'output-models',
                        Component: ProjectFinetuningOutputModelsPage,
                      },
                      { path: '*', loader: () => redirect('overview') },
                    ],
                  },
                  { path: '*', loader: () => redirect('list') },
                ],
              },
              {
                path: 'model',
                children: [
                  { path: 'list', Component: ProjectModelListPage },
                  {
                    path: ':modelId/*',
                    Component: ProjectModelDetailLayout,
                    children: [
                      { path: 'overview', Component: ProjectModelOverviewPage },
                      ...hideOptionFromRelease({
                        path: 'versions',
                        Component: ProjectModelVersionsPage,
                      }),
                      {
                        path: 'endpoints',
                        Component: ProjectModelEndpointsPage,
                      },
                      {
                        path: 'files',
                        Component: ProjectModelFilesPage,
                      },
                      { path: '*', loader: () => redirect('overview') },
                    ],
                  },
                  { path: '*', loader: () => redirect('list') },
                ],
              },
              {
                path: 'dataset',
                children: [
                  { path: 'list', Component: ProjectDatasetListPage },
                  { path: 'create', Component: ProjectDatasetCreatePage },
                  ...hideOptionFromRelease({
                    path: ':datasetId/*',
                    Component: ProjectDatasetDetailLayout,
                    children: [
                      {
                        path: 'overview',
                        Component: ProjectDatasetOverviewPage,
                      },
                      { path: '*', loader: () => redirect('overview') },
                    ],
                  }),
                  { path: '*', loader: () => redirect('list') },
                ],
              },
              {
                path: 'document',
                Component: () => <FlagValidationWrapper flagKey="dedicatedEndpointsRag" />,
                children: [
                  { path: 'list', Component: ProjectDocumentListPage },
                  { path: '*', loader: () => redirect('list') },
                ],
              },
              {
                path: 'monthly-usage',
                Component: () => <FlagValidationWrapper flagKey="dedicatedEndpointsMonthlyUsage" />,
                children: [{ index: true, Component: ProjectMonthlyUsagePage }],
              },
              { path: 'settings', Component: ProjectSettingsPage },
              { path: '*', loader: () => redirect('endpoint') },
            ],
          },
        ],
      },
      { path: '*', loader: () => redirect('list') },
    ],
  },
  { path: '*', loader: () => redirect('landing') },
];

export default dedicatedEndpointsRoutes;
